import type { QueryClient } from '@tanstack/svelte-query';
import { getQuery } from '#lib/query-utils/getQuery.js';
import {
	EDITIONS_GALLERY_LIMIT,
	EDITIONS_GALLERY_STALE_TIME,
} from '#lib/websites/editions/constants/gallery.js';
import {
	EDITIONS_GALLERY_DEFAULT_SORTS_ASC,
	EditionsGallerySort,
} from '#lib/websites/editions/constants/sorting.js';
import { getEditionsSortModifier } from '#lib/websites/editions/pages/EditionsGalleryPage/utils/getEditionsSortModifier/getEditionsSortModifier.js';
import {
	GetEditionsGalleryDocument,
	type GetEditionsGalleryQuery,
	type GetEditionsGalleryQueryVariables,
} from '#lib/websites/editions/queries/__generated__/getEditionsGallery.generated.js';
import { editionsGalleryFilterCustomAttributes } from '#lib/websites/editions/utils/getEditionsGalleryFilterSections/index.js';
import type { FilterSections } from '$global/features/filter-drawer/types';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';

// Re-exported from the shared constants file so the REST and GraphQL
// flavours of the gallery loaders page identically
// (rest-migration/feature-flags.md).
export {
	EDITIONS_GALLERY_LIMIT,
	EDITIONS_GALLERY_STALE_TIME,
} from '#lib/websites/editions/constants/gallery.js';
export const EDITIONS_GALLERY_BASE_FILTER: GetEditionsGalleryQueryVariables['filter'] =
	{
		_and: [
			{
				_or: [
					// A series flagged preview-only collapses to a single card: its
					// first edition stands in for the whole series.
					{
						_and: [
							{
								code: {
									_ends_with: '-1',
								},
							},
							{
								series: {
									only_show_preview_in_gallery: {
										_eq: true,
									},
								},
							},
						],
					},
					// A sub-series (e.g. The Beautiful Paintings H12-1 … H12-8) also
					// collapses to a single card: its first edition stands in for the
					// sub-series and the card links to the sub-series page. Keeping
					// the stand-in inside the one Editions query is what lets the
					// sort, filters, paging and result count treat sub-series cards
					// and edition cards as one list.
					{
						_and: [
							{
								code: {
									_ends_with: '-1',
								},
							},
							{
								series: {
									parent_series_func: {
										count: {
											_gt: '0',
										},
									},
								},
							},
						],
					},
					// Every other edition of a top-level series gets its own card.
					{
						_and: [
							{
								series: {
									only_show_preview_in_gallery: {
										_eq: false,
									},
								},
							},
							{
								series: {
									parent_series_func: {
										count: {
											_eq: '0',
										},
									},
								},
							},
						],
					},
					{
						series: {
							id: {
								_null: true,
							},
						},
					},
				],
			},
			// Editions attached directly to a parent series are skipped: the
			// parent is represented by its sub-series cards.
			{
				series: {
					sub_series_func: {
						count: {
							_eq: '0',
						},
					},
				},
			},
		],
	};
export const EDITIONS_GALLERY_BASE_VARIABLES: GetEditionsGalleryQueryVariables =
	{
		limit: EDITIONS_GALLERY_LIMIT,
		offset: 0,
		sort: [EditionsGallerySort.Newest, ...EDITIONS_GALLERY_DEFAULT_SORTS_ASC],
		filter: {
			_and: [EDITIONS_GALLERY_BASE_FILTER],
		},
	};

/**
 * Shared universal-load logic for pages embedding the editions gallery
 * browse grid (the gallery page and the editions homepage): seeds the
 * query cache with the server-fetched first page, applies the filter
 * drawer's URL params and prefetches the filtered gallery query.
 */
export const prepareEditionsGalleryBrowse = ({
	queryClient,
	filterSections,
	editions,
	searchParams,
}: {
	queryClient: QueryClient;
	filterSections: FilterSections;
	editions: GetEditionsGalleryQuery;
	searchParams: URLSearchParams;
}): GetEditionsGalleryQueryVariables => {
	queryClient.setQueryData(
		getQueryKeys(GetEditionsGalleryDocument, EDITIONS_GALLERY_BASE_VARIABLES),
		{
			pageParams: [0],
			pages: [editions],
		}
	);

	const filter = getAppliedFilters({
		searchParams,
		filterSections,
		customAttributes: editionsGalleryFilterCustomAttributes,
	});

	const sort = getAppliedSort({
		searchParams,
		defaultSort: [EditionsGallerySort.Newest],
	});

	const sortModifier = getEditionsSortModifier(sort[0]);

	const variables: GetEditionsGalleryQueryVariables = {
		...EDITIONS_GALLERY_BASE_VARIABLES,
		sort: sortModifier,
		filter: {
			_and: [EDITIONS_GALLERY_BASE_FILTER, ...filter],
		},
	};

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetEditionsGalleryDocument, variables),
		initialPageParam: 0,
		staleTime: EDITIONS_GALLERY_STALE_TIME,
	});

	return variables;
};
